import React, { useState, useEffect, useRef } from 'react'
import {
  Form,
  Input,
  Select,
  Steps,
  Card,
  Button,
  message,
  InputNumber,
  Modal,
  Result,
  Space,
  DatePicker,
  Tooltip
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { postTopupWithDuplicateConfirm } from '../utils/duplicateTopup'
import dayjs from 'dayjs'
import { Divider } from 'antd'
import TopupSchemeKeyInput from '../components/TopupSchemeKeyInput'
import { now as tzNow } from '../utils/timezone'

const { Option } = Select

const PageContainer = styled.div`
  min-height: 100vh;
`

const StepsContainer = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-steps {
    .ant-steps-item {
      flex: 1;
      min-width: 0;
    }

    .ant-steps-item-title {
      font-size: 14px;

      @media (max-width: 768px) {
        font-size: 12px;
      }
    }

    @media (max-width: 768px) {
      .ant-steps-item-content {
        padding-right: 8px;
      }
    }
  }
`

const FormCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-card-head {
    background: linear-gradient(135deg, #fff5f7 0%, #ffeef2 100%);
    border-bottom: 2px solid #d5728a;

    .ant-card-head-title {
      font-size: 18px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`

const TabButton = styled(Button)`
  margin-bottom: 16px;

  &.active {
    background: #d5728a;
    border-color: #d5728a;
    color: #fff;
  }
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

// 付款明細專用：每行兩個欄位（現金/匯款 一組、應收款/行動支付 一組）
const PaymentFieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const CustomerOptionItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }

  .customer-name {
    font-weight: 500;
    color: #1d1d1f;
    margin-bottom: 4px;
  }

  .customer-details {
    font-size: 12px;
    color: #6c757d;
  }
`

const ValueAddedPurchasePage = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { user } = useAuth()
  const businessId = user.businessId
  const [currentStep, setCurrentStep] = useState(0)
  const [customerMode, setCustomerMode] = useState('quick') // 'quick' or 'full'
  const [employees, setEmployees] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentBalance, setCurrentBalance] = useState(0)
  const [balanceInfo, setBalanceInfo] = useState(null) // 完整餘額資訊（含 balanceByScheme）
  const [valueAddedAmount, setValueAddedAmount] = useState(0)
  const [discount, setDiscount] = useState(100)
  const [cash, setCash] = useState(0)
  const [bankTransfer, setBankTransfer] = useState(0)
  const [accountsReceivable, setAccountsReceivable] = useState(0)
  const [paymentMethod, setPaymentMethod] = useState('由店務系統付費')
  const [mobileCardSwipe, setMobileCardSwipe] = useState(0) // 已棄用：保留向後相容
  const [cardPayment, setCardPayment] = useState(0)
  const [mobilePayment, setMobilePayment] = useState(0)
  const [paymentFee, setPaymentFee] = useState(0) // 自動算 + 可手動覆寫
  const [paymentFeeSettings, setPaymentFeeSettings] = useState({
    cardFeeRate: 0,
    mobilePaymentFeeRate: 0,
    autoCalculate: true
  })
  const [purchaseCompleted, setPurchaseCompleted] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState(null)
  const [customerOptions, setCustomerOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [showCustomerList, setShowCustomerList] = useState(false)
  const [showPhoneCustomerList, setShowPhoneCustomerList] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const [customerNameValue, setCustomerNameValue] = useState('')
  const [selectedYear, setSelectedYear] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  const searchTimeoutRef = useRef(null)
  // 會員等級和等級儲值回饋相關狀態
  const [membershipTiers, setMembershipTiers] = useState([]) // 會員等級列表
  const [customerMembershipTier, setCustomerMembershipTier] = useState(null) // 客戶會員等級
  const [customerTierAccumulated, setCustomerTierAccumulated] = useState(0) // 客戶累計實付金額（用於判斷升等資格）
  const [selectedTopupBonus, setSelectedTopupBonus] = useState(null) // 選中的等級儲值回饋檔位
  const [topupBonusOptedOut, setTopupBonusOptedOut] = useState(false) // 使用者主動選擇不套用活動方案

  // 撈商家金流手續費設定（與結帳頁邏輯一致）
  const fetchPaymentFeeSettings = async () => {
    try {
      const res = await api.get(`/api/business_B/${businessId}`)
      const settings = res?.data?.paymentFeeSettings
      if (settings) {
        setPaymentFeeSettings({
          cardFeeRate: Number(settings.cardFeeRate) || 0,
          mobilePaymentFeeRate: Number(settings.mobilePaymentFeeRate) || 0,
          autoCalculate: settings.autoCalculate !== false
        })
      }
    } catch (err) {
      console.warn('讀取金流手續費設定失敗，使用 0%:', err.message)
    }
  }

  useEffect(() => {
    fetchEmployees()
    fetchProducts()
    fetchMembershipTiers()
    fetchPaymentFeeSettings()

    // 初始化當前日期
    const today = tzNow()
    const currentYear = today.year()
    const currentMonth = today.month() + 1 // dayjs month is 0-based
    const currentDay = today.date()

    setSelectedYear(currentYear)
    setSelectedMonth(currentMonth)
    setSelectedDay(currentDay)

    // 設定表單預設日期值
    const defaultDate = today.format('YYYY-MM-DD')
    form.setFieldsValue({ date: defaultDate })

    // 清理定時器
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const fetchEmployees = async () => {
    try {
      const response = await api.get(`/api/employees/${businessId}`)
      const allEmployees = response.data || []

      // 過濾掉店家（businessRole 為 'owner' 的員工）
      const filteredEmployees = allEmployees

      setEmployees(filteredEmployees)

      // 獲取當前選擇的銷售人員
      const currentSalesPersonnel = form.getFieldValue('salesPersonnel')
      const filteredEmployeeNames = filteredEmployees.map((emp) => emp.name)

      // 如果當前選擇的值不在過濾後的員工列表中（可能是 owner 被過濾掉了），則清除並選擇第一個
      if (
        currentSalesPersonnel &&
        !filteredEmployeeNames.includes(currentSalesPersonnel)
      ) {
        if (filteredEmployees.length > 0) {
          const firstEmployeeName = filteredEmployees[0].name

          form.setFieldsValue({
            salesPersonnel: firstEmployeeName
          })
        } else {
          // 如果沒有可選員工，清除選擇
          form.setFieldsValue({
            salesPersonnel: undefined
          })
        }
      } else if (filteredEmployees.length > 0 && !currentSalesPersonnel) {
        // 如果沒有選擇且沒有值，預設選擇第一個
        const firstEmployeeName = filteredEmployees[0].name
        form.setFieldsValue({
          salesPersonnel: firstEmployeeName
        })
      }
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  const fetchProducts = async () => {
    try {
      const response = await api.get(`/api/business_B/${businessId}/products`)
      setProducts(response.data || [])
    } catch (error) {
      console.error('獲取服務列表失敗:', error)
    }
  }

  // 獲取會員等級列表
  const fetchMembershipTiers = async () => {
    if (!businessId) return

    try {
      const response = await api.get(
        `/api/business_B/${businessId}/membership-tiers`
      )
      if (response.data.success) {
        const tiersData = response.data.data?.tiers || []
        const enabledTiers = tiersData.filter((t) => t.isEnabled !== false)
        setMembershipTiers(enabledTiers)
      }
    } catch (error) {
      console.error('獲取會員等級列表失敗:', error)
    }
  }

  // 獲取客戶會員等級 + 累計實付金額
  const fetchCustomerMembershipTier = async (customerId) => {
    if (!customerId || !businessId) {
      setCustomerMembershipTier(null)
      setCustomerTierAccumulated(0)
      return
    }

    try {
      const response = await api.get(
        `/api/businesses/${businessId}/customers/${customerId}`
      )
      const customer = response.data?.data || response.data
      const tierName =
        customer?.businessSpecificData?.membershipLevel ||
        customer?.businessRelations?.[0]?.businessSpecificData
          ?.membershipLevel ||
        customer?.membershipLevel ||
        null
      const accumulated = Number(
        customer?.businessSpecificData?.tierAccumulatedAmount ??
          customer?.businessRelations?.[0]?.businessSpecificData
            ?.tierAccumulatedAmount ??
          0
      )
      setCustomerMembershipTier(tierName)
      setCustomerTierAccumulated(Number.isFinite(accumulated) ? accumulated : 0)
    } catch (error) {
      console.error('獲取客戶會員等級失敗:', error)
      setCustomerMembershipTier(null)
      setCustomerTierAccumulated(0)
    }
  }

  // 當會員等級列表加載完成且客戶已選擇時，獲取客戶會員等級
  useEffect(() => {
    if (membershipTiers.length > 0 && selectedCustomerId) {
      fetchCustomerMembershipTier(selectedCustomerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipTiers, selectedCustomerId])

  const searchCustomers = async (searchTerm, source = 'name') => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerOptions([])
      setShowCustomerList(false)
      setShowPhoneCustomerList(false)
      return
    }

    // 清除之前的定時器
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // 設定防抖，300ms 後執行搜尋
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const response = await api.get(
          `/api/business_B/${businessId}/customers/search`,
          {
            params: { searchTerm }
          }
        )
        if (response.data.success) {
          const customers = response.data.data || []
          const customerList = customers.map((customer) => {
            const name = customer.name || customer.userInfo?.name || ''
            const phone = customer.phone || customer.userInfo?.phone || ''
            const email = customer.email || customer.userInfo?.email || ''
            return {
              value: customer._id || customer.userInfo?._id,
              label: `${name} ${phone}`.trim() || '未命名客戶',
              customer: customer,
              name: name,
              phone: phone,
              email: email
            }
          })
          setCustomerOptions(customerList)
          if (source === 'name') {
            setShowCustomerList(customerList.length > 0)
            setShowPhoneCustomerList(false)
          } else {
            setShowPhoneCustomerList(customerList.length > 0)
            setShowCustomerList(false)
          }
        } else {
          setCustomerOptions([])
          setShowCustomerList(false)
          setShowPhoneCustomerList(false)
        }
      } catch (error) {
        console.error('搜尋客戶失敗:', error)
        setCustomerOptions([])
        setShowCustomerList(false)
        setShowPhoneCustomerList(false)
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  const handleCustomerSelect = async (value, option) => {
    const customer = option?.customer
    if (customer) {
      // 自動填充客戶資訊
      const birthday = customer.birthday
        ? customer.birthday.includes('-')
          ? customer.birthday.split('-').slice(1).join('-')
          : customer.birthday
        : undefined

      const customerName = customer.name || customer.userInfo?.name || ''
      setCustomerNameValue(customerName)

      form.setFieldsValue({
        customerName: customerName,
        phone: customer.phone || customer.userInfo?.phone,
        customerBirthday: birthday,
        customerType:
          customer.businessSpecificData?.membershipLevel || '一般會員',
        gender: customer.gender || customer.userInfo?.gender || '女'
      })

      // 獲取客戶的當前儲值金餘額
      try {
        const customerId = customer._id || customer.userInfo?._id
        if (customerId) {
          // 保存選中的客戶ID
          setSelectedCustomerId(customerId)

          // 使用與 CustomerDetail.js 一致的餘額 API
          const response = await api.get(
            `/api/businesses/${businessId}/customers/${customerId}/balance`
          )
          const data = response.data?.data || response.data
          const balance = data?.balance ?? response.data?.balance ?? 0
          setCurrentBalance(balance)
          setBalanceInfo(data || null)

          // 獲取客戶會員等級
          fetchCustomerMembershipTier(customerId)
        }
      } catch (error) {
        console.error('獲取儲值金餘額失敗:', error)
        setCurrentBalance(0)
        setBalanceInfo(null)
      }

      // 如果選擇了客戶，清空選項列表
      setCustomerOptions([])
      setShowCustomerList(false)
      setShowPhoneCustomerList(false)
    }
  }

  const handlePhoneSearch = async (phone) => {
    if (!phone || phone.length < 4) {
      setShowPhoneCustomerList(false)
      setCustomerOptions([])
      return
    }
    await searchCustomers(phone, 'phone')
  }

  const onFinish = async (values) => {
    // 檢查是否在最後一步，如果不是則阻止提交
    if (currentStep < steps.length - 1) {
      // 如果不在最後一步，觸發表單提交可能是因為按了 Enter，應該執行下一步
      handleNext()
      return
    }
    // 如果是最後一步，也不在這裡提交，由 handleSubmit 處理
  }

  const handleSubmit = async (e) => {
    // 阻止表單默認提交行為
    if (e) {
      e.preventDefault()
    }

    // ── 防重複送出：若正在處理中則直接 return ────────────────────────────────
    if (loading) return

    // 檢查是否在最後一步
    if (currentStep < steps.length - 1) {
      handleNext()
      return
    }

    // 驗證表單
    try {
      await form.validateFields()
    } catch (error) {
      message.error('請完成必填欄位')
      return
    }

    // 使用表單例項取值，確保包含未掛載欄位（跨步驟）
    const allValues = form.getFieldsValue(true)

    setLoading(true)
    try {
      // 檢查是否有匹配的等級儲值回饋（搜尋所有啟用的等級）- 需要在計算 totalPayment 之前
      let bonusAmount = 0
      if (topupBonusOptedOut) {
        // 使用者主動選擇不套用活動方案，bonusAmount 維持 0
      } else if (selectedTopupBonus) {
        bonusAmount = Number(selectedTopupBonus.bonus) || 0
      } else {
        // 如果沒有手動選擇等級儲值回饋，嘗試自動匹配
        membershipTiers.forEach((tier) => {
          if (tier.isEnabled && tier.topupBonuses) {
            const matchedBonus = tier.topupBonuses.find(
              (bonus) => Number(bonus.amount) === Number(valueAddedAmount)
            )
            if (matchedBonus) {
              // 如果匹配到多個等級（可能性較低），以等級更高的為準（假設 order 越小等級越高，或者簡單取最高 bonus）
              if (matchedBonus.bonus >= bonusAmount) {
                bonusAmount = Number(matchedBonus.bonus) || 0
              }
            }
          }
        })
      }

      // 驗證付費資訊總和
      // 總付款金額 = 客戶實際需要支付的金額（不包含贈送金額）
      const totalPayment =
        valueAddedAmount && discount
          ? Math.round(valueAddedAmount * (discount / 100))
          : 0

      const cashAmount = allValues.cash || 0
      const bankTransferAmount = allValues.bankTransfer || 0
      const accountsReceivableAmount = allValues.accountsReceivable || 0
      // 新版分開兩欄：刷卡 + 行動支付（合計取代舊 mobileCardSwipe）
      const cardPaymentAmount = Number(allValues.cardPayment) || 0
      const mobilePaymentAmountNew = Number(allValues.mobilePayment) || 0
      const mobilePaymentAmount = cardPaymentAmount + mobilePaymentAmountNew

      const sum =
        cashAmount +
        bankTransferAmount +
        accountsReceivableAmount +
        mobilePaymentAmount

      if (totalPayment > 0 && sum !== totalPayment) {
        Modal.error({
          title: '付款方式總和與總付款金額不符',
          content: '請確認加總金額是否正確',
          onOk: () => {
            setLoading(false)
          }
        })
        return
      }

      // 驗證必填欄位
      if (!allValues.customerName || !allValues.phone) {
        Modal.error({
          title: '客戶資訊不完整',
          content: '請輸入客戶名稱和電話'
        })
        setLoading(false)
        return
      }

      // 如果沒有選擇客戶ID（手動輸入的情況），仍然可以提交，但需要標記
      // 後端可能需要根據 customerName 和 phone 查找或創建客戶

      // 員工選的等級名稱（後端會嚴格驗證資格才升等；不符合條件就不升）
      const finalSelectedTierName = selectedTopupBonus?.tierName || null

      // 構建請求體（參考 CheckoutPage.js 的格式）
      const purchaseData = {
        customerId: selectedCustomerId || undefined, // 如果沒有選擇客戶，後端可能需要根據名稱和電話查找
        customerName: allValues.customerName,
        phone: allValues.phone,
        email: allValues.email || undefined,
        salesPersonnel: allValues.salesPersonnel || user.name,
        // 參考 CheckoutPage.js，使用 amount 和 bonusAmount
        amount: valueAddedAmount, // 實際儲值金額
        // 如果有匹配的贈送金額，一併提交給後端（後端會根據這個值加到餘額中）
        // 確保 bonusAmount 被提交（即使為 0 也要明確提交，讓後端知道沒有贈送）
        bonusAmount: bonusAmount,
        // 使用者主動選擇不套用活動方案時，通知後端跳過 bonus 自動計算
        ...(topupBonusOptedOut ? { bonusOptedOut: true } : {}),
        // 員工最終選擇的會員等級（原本選擇或經提醒升等後的較高等級）
        ...(finalSelectedTierName
          ? { selectedTierName: finalSelectedTierName }
          : {}),
        // 保留原有字段以兼容 API
        valueAddedAmount: valueAddedAmount,
        discount: discount,
        totalPayment: totalPayment, // 總付款金額（客戶實際需要支付的金額，不包含贈送金額）
        actualChargeAmount: totalPayment, // 實際收費金額（與總付款金額相同）
        currentBalance: currentBalance,
        balanceAfterValueAdded: currentBalance + valueAddedAmount + bonusAmount, // 儲值後餘額（包含贈送金額）
        paymentMethod: paymentMethod,
        paymentDetails: {
          cash: cashAmount,
          bankTransfer: bankTransferAmount,
          accountsReceivable: accountsReceivableAmount,
          // 新版分開兩欄：刷卡 + 行動支付
          cardPayment: Number(allValues.cardPayment) || 0,
          mobilePayment: Number(allValues.mobilePayment) || 0,
          // 雙寫舊欄位（向後相容報表/查詢）
          mobileCardSwipe:
            (Number(allValues.cardPayment) || 0) +
            (Number(allValues.mobilePayment) || 0)
        },
        // 金流手續費（前端覆寫；後端若未帶會自動算）
        paymentFee: Number(allValues.paymentFee) || 0,
        cardFeeRate: paymentFeeSettings.cardFeeRate,
        mobilePaymentFeeRate: paymentFeeSettings.mobilePaymentFeeRate,
        date: allValues.date || tzNow().format('YYYY-MM-DD'),
        note: allValues.note || undefined,
        ...(allValues.purchaseSchemeKey?.trim()
          ? { schemeKey: allValues.purchaseSchemeKey.trim() }
          : {}),
        ...(allValues.purchaseExpiryDate
          ? {
              expiryDate: dayjs(allValues.purchaseExpiryDate).format(
                'YYYY-MM-DD'
              )
            }
          : {}),
        ...(allValues.assignTierName?.trim()
          ? { assignTierName: allValues.assignTierName.trim() }
          : {})
      }

      // 調試：確認 bonusAmount 是否正確
      console.log('提交儲值購買數據:', {
        amount: purchaseData.amount,
        bonusAmount: purchaseData.bonusAmount,
        valueAddedAmount: purchaseData.valueAddedAmount,
        selectedTopupBonus: selectedTopupBonus,
        balanceAfterValueAdded: purchaseData.balanceAfterValueAdded
      })

      // 調用 API 提交儲值購買（遇到「可能重複儲值」時先請店家確認，取消就不送）
      const response = await postTopupWithDuplicateConfirm(
        api,
        `/api/businesses/${businessId}/value-added/purchase`,
        purchaseData
      )
      if (!response) return

      // 處理 API 響應
      if (
        response.data?.success !== false &&
        response.status >= 200 &&
        response.status < 300
      ) {
        // 顯示完成提示
        setPurchaseCompleted(true)
        message.success(response.data?.message || '儲值購買成功！')

        // 3秒後跳轉到儲值記錄頁面
        setTimeout(() => {
          navigate('/value-added/records')
        }, 3000)
      } else {
        // API 返回錯誤
        const errorMessage =
          response.data?.message ||
          response.data?.error ||
          '儲值購買失敗，請稍後再試'
        Modal.error({
          title: '儲值購買失敗',
          content: errorMessage
        })
      }
    } catch (error) {
      // 處理網絡錯誤或其他異常
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        '儲值購買失敗，請稍後再試'

      Modal.error({
        title: '儲值購買失敗',
        content: errorMessage
      })
      console.error('儲值購買錯誤:', error)
    } finally {
      setLoading(false)
    }
  }

  const steps = [
    {
      title: '客戶資訊'
    },
    {
      title: '輸入儲值費用'
    },
    {
      title: '選擇日期'
    }
  ]

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <FormCard title='客戶資訊'>
            <FormRow>
              <Form.Item
                name='customerName'
                label='客戶名稱'
                rules={[{ required: true, message: '請輸入客戶名稱' }]}
              >
                <div
                  className='customer-search-container'
                  style={{ position: 'relative' }}
                >
                  <Input
                    value={customerNameValue}
                    onChange={(e) => {
                      const value = e.target.value
                      setCustomerNameValue(value)
                      form.setFieldsValue({ customerName: value })
                      // 只有在輸入法組合結束後才觸發搜尋
                      if (!isComposing) {
                        searchCustomers(value, 'name')
                      }
                    }}
                    onCompositionStart={() => {
                      setIsComposing(true)
                    }}
                    onCompositionEnd={(e) => {
                      setIsComposing(false)
                      // 輸入法組合結束後觸發搜尋
                      const value = e.target.value
                      searchCustomers(value, 'name')
                    }}
                    placeholder='輸入姓名或是電話(0912xxx至少兩個數字)搜尋'
                    size='large'
                    allowClear
                  />
                  {showCustomerList && customerOptions.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}
                    >
                      {customerOptions.map((option) => (
                        <CustomerOptionItem
                          key={option.value}
                          onClick={() =>
                            handleCustomerSelect(option.value, option)
                          }
                        >
                          <div className='customer-name'>
                            {option.name || '未命名客戶'}
                          </div>
                          <div className='customer-details'>
                            {option.phone}
                            {option.email && ` • ${option.email}`}
                          </div>
                        </CustomerOptionItem>
                      ))}
                    </div>
                  )}
                  {searching && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        padding: '12px',
                        textAlign: 'center',
                        color: '#666',
                        marginTop: '4px'
                      }}
                    >
                      搜尋中...
                    </div>
                  )}
                </div>
              </Form.Item>

              <Form.Item
                name='phone'
                label='電話'
                rules={[{ required: true, message: '請輸入電話' }]}
              >
                <div
                  className='phone-search-container'
                  style={{ position: 'relative' }}
                >
                  <Input
                    value={form.getFieldValue('phone') || ''}
                    onChange={(e) => {
                      form.setFieldsValue({ phone: e.target.value })
                      handlePhoneSearch(e.target.value)
                    }}
                    placeholder='請輸入電話號碼'
                    size='large'
                    allowClear
                  />
                  {showPhoneCustomerList && customerOptions.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}
                    >
                      {customerOptions.map((option) => (
                        <CustomerOptionItem
                          key={option.value}
                          onClick={() =>
                            handleCustomerSelect(option.value, option)
                          }
                        >
                          <div className='customer-name'>
                            {option.name || '未命名客戶'}
                          </div>
                          <div className='customer-details'>
                            {option.phone}
                            {option.email && ` • ${option.email}`}
                          </div>
                        </CustomerOptionItem>
                      ))}
                    </div>
                  )}
                  {searching && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        padding: '12px',
                        textAlign: 'center',
                        color: '#666',
                        marginTop: '4px'
                      }}
                    >
                      搜尋中...
                    </div>
                  )}
                </div>
              </Form.Item>
            </FormRow>
          </FormCard>
        )
      case 1:
        return (
          <FormCard title='輸入儲值費用'>
            <Form.Item label='目前儲值金餘額'>
              <Input
                disabled
                value={
                  currentBalance > 0
                    ? `NT$ ${currentBalance.toLocaleString()}`
                    : '-'
                }
                suffix='元'
              />
            </Form.Item>
            {balanceInfo?.balanceByScheme?.length > 0 && (
              <Form.Item label='各儲值活動餘額'>
                <div
                  style={{
                    padding: '10px 12px',
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 8,
                    fontSize: 13
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {balanceInfo.balanceByScheme.map((s) => (
                      <li key={s.schemeKey} style={{ marginBottom: 4 }}>
                        {s.schemeKey} NT${' '}
                        {Number(s.balance || 0).toLocaleString()}
                        {(s.expiryDateDisplay || s.expiryDate) &&
                          `（期限 ${s.expiryDateDisplay || dayjs(s.expiryDate).format('YYYY/MM/DD')}）`}
                      </li>
                    ))}
                  </ul>
                </div>
              </Form.Item>
            )}

            {/* 儲值活動與使用期限（選填） */}
            <Form.Item name='purchaseSchemeKey' label='儲值活動（選填）'>
              <TopupSchemeKeyInput
                businessId={businessId}
                placeholder='可輸入或從下拉選擇曾用過的儲值活動'
                extraSchemeKeys={
                  balanceInfo?.balanceByScheme
                    ?.map((s) => s.schemeKey)
                    .filter(Boolean) ?? []
                }
              />
            </Form.Item>
            <Form.Item
              name='purchaseExpiryDate'
              label={
                <span>
                  使用期限 / 會籍到期日（選填）
                </span>
              }
              extra='填入後，儲值金方案到期日與客戶會籍到期日皆會更新為此日期'
            >
              <DatePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD'
                placeholder='選擇到期日'
              />
            </Form.Item>

            {/* 指派會員等級（選填） */}
            {membershipTiers.length > 0 && (
              <Form.Item
                name='assignTierName'
                label='指派會員等級（選填）'
                extra={
                  customerMembershipTier
                    ? `目前等級：${customerMembershipTier}，選擇後將直接更新等級`
                    : '選擇後將直接指派等級給客戶'
                }
              >
                <Select placeholder='不變更等級' allowClear>
                  {membershipTiers
                    .filter((t) => t.isEnabled)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((t) => (
                      <Option key={t.name} value={t.name}>
                        {t.name}
                        {t.discountRate && t.discountRate < 100
                          ? `（${t.discountRate}折）`
                          : ''}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item name='salesPersonnel' label='銷售人員'>
              <Select
                placeholder='請選擇銷售人員'
                allowClear
                value={form.getFieldValue('salesPersonnel')}
                onChange={(value) => {
                  form.setFieldsValue({ salesPersonnel: value })
                }}
                onFocus={() => {
                  const currentValue = form.getFieldValue('salesPersonnel')
                }}
              >
                {console.log('employees', employees)}
                {employees.map((emp) => (
                  <Option key={emp._id} value={emp.name}>
                    {emp.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name='valueAddedAmount'
              label='本次儲值金額'
              rules={[{ required: true, message: '請輸入儲值金額' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                precision={0}
                placeholder='請輸入金額'
                prefix='NT$'
                value={valueAddedAmount}
                onChange={(val) => {
                  const amount = val || 0
                  setValueAddedAmount(amount)
                  form.setFieldsValue({ valueAddedAmount: amount })
                  setSelectedTopupBonus(null) // 手動輸入時清除選定的等級儲值回饋
                  setTopupBonusOptedOut(false) // 手動輸入新金額時，重置不套用狀態
                  // 手動輸入金額時自動匹配（optedOut 已被重置，使用本地判斷避免非同步問題）
                  if (amount) {
                    let matchedBonus = null
                    membershipTiers.forEach((tier) => {
                      if (tier.isEnabled && tier.topupBonuses) {
                        const bonus = tier.topupBonuses.find(
                          (b) => Number(b.amount) === Number(amount)
                        )
                        if (bonus) {
                          // 如果手動輸入，且當前客戶有對應等級，優先匹配當前等級
                          if (tier.name === customerMembershipTier) {
                            matchedBonus = { ...bonus, tierName: tier.name }
                          } else if (!matchedBonus) {
                            matchedBonus = { ...bonus, tierName: tier.name }
                          }
                        }
                      }
                    })
                    if (matchedBonus) {
                      setSelectedTopupBonus(matchedBonus)
                    }
                  }
                }}
              />
            </Form.Item>

            {/* 快速選擇按鈕（彙整所有等級的等級儲值回饋） */}
            <Form.Item label='選擇等級儲值回饋（選填）'>
              <div>
                <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
                  等級儲值回饋（點選後自動帶入金額）：
                </div>
                <Space wrap style={{ width: '100%' }}>
                  {(() => {
                    // 獲取所有等級的等級儲值回饋檔位
                    const allBonuses = []
                    membershipTiers.forEach((tier) => {
                      if (tier.isEnabled && tier.topupBonuses) {
                        tier.topupBonuses.forEach((bonus) => {
                          allBonuses.push({
                            ...bonus,
                            tierName: tier.name
                          })
                        })
                      }
                    })

                    if (allBonuses.length === 0) {
                      return (
                        <span style={{ fontSize: 12, color: '#999' }}>
                          目前未設置任何等級儲值回饋
                        </span>
                      )
                    }

                    // 按金額排序
                    const sortedBonuses = allBonuses.sort(
                      (a, b) => Number(a.amount) - Number(b.amount)
                    )

                    return sortedBonuses.map((bonus, index) => {
                      const amount = Number(bonus.amount) || 0
                      const bonusAmount = Number(bonus.bonus) || 0
                      // 檢查是否選中（同時匹配金額與等級，因為同金額可能在不同等級各有一組回饋檔位）
                      const isSelected =
                        selectedTopupBonus &&
                        selectedTopupBonus.amount === amount &&
                        selectedTopupBonus.tierName === bonus.tierName

                      return (
                        <Button
                          key={index}
                          type={isSelected ? 'primary' : 'default'}
                          onClick={() => {
                            setValueAddedAmount(amount)
                            form.setFieldsValue({ valueAddedAmount: amount })
                            setSelectedTopupBonus(bonus)
                            setTopupBonusOptedOut(false)
                          }}
                          style={{
                            borderRadius: 8,
                            height: 44,
                            padding: '0 12px',
                            fontSize: 13,
                            textAlign: 'left',
                            borderColor: isSelected ? undefined : '#d9d9d9',
                            boxShadow: isSelected
                              ? '0 2px 4px rgba(0,0,0,0.1)'
                              : 'none'
                          }}
                        >
                          <div style={{ lineHeight: '1.2' }}>
                            <span style={{ fontWeight: 600 }}>
                              儲 {amount.toLocaleString()} 送{' '}
                              {bonusAmount.toLocaleString()}
                            </span>
                            <br />
                            <span style={{ fontSize: 10, opacity: 0.7 }}>
                              (等級：{bonus.tierName})
                            </span>
                          </div>
                        </Button>
                      )
                    })
                  })()}
                  {/* 不套用活動方案按鈕 */}
                  <Button
                    type={topupBonusOptedOut ? 'primary' : 'default'}
                    danger={topupBonusOptedOut}
                    onClick={() => {
                      setTopupBonusOptedOut(true)
                      setSelectedTopupBonus(null)
                    }}
                    style={{
                      borderRadius: 8,
                      height: 44,
                      padding: '0 12px',
                      fontSize: 13,
                    }}
                  >
                    不套用活動方案
                  </Button>
                </Space>
                {/* 顯示當前選擇之等級儲值回饋的詳細資訊 */}
                {valueAddedAmount > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      background: '#f6ffed',
                      borderRadius: 8,
                      border: '1px solid #b7eb8f',
                      fontSize: 13,
                      color: '#389e0d'
                    }}
                  >
                    {(() => {
                      const matchedBonus = selectedTopupBonus

                      if (matchedBonus) {
                        const bonusAmount = Number(matchedBonus.bonus) || 0
                        const totalAmount = valueAddedAmount + bonusAmount
                        // 員工主動選等級回饋方案：只看 topupUpgradeAmount（單筆門檻）
                        // upgradeAmount（累計實付）是結帳自動升等用的，不限制儲值購買升等
                        const targetTier = membershipTiers.find(
                          (t) => t.name === matchedBonus.tierName
                        )
                        const topupRule =
                          Number(targetTier?.topupUpgradeAmount) || 0
                        // topupRule=0 代表無單筆門檻限制，直接允許升等
                        const willUpgrade =
                          topupRule === 0 || Number(valueAddedAmount) >= topupRule
                        return (
                          <>
                            <div style={{ fontWeight: 500 }}>
                              等級儲值回饋匹配：{matchedBonus.tierName} 優惠檔位
                            </div>
                            <div style={{ marginTop: 4 }}>
                              贈送金額：NT$ {bonusAmount.toLocaleString()}
                            </div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                              預計到帳：NT$ {totalAmount.toLocaleString()}
                            </div>
                            {willUpgrade ? (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 11,
                                  fontStyle: 'italic',
                                  opacity: 0.85
                                }}
                              >
                                * 儲值成功後，客戶等級將升等為「
                                {matchedBonus.tierName}」
                              </div>
                            ) : (
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: '#d4380d',
                                  lineHeight: 1.6
                                }}
                              >
                                * 本次儲值 NT$ {Number(valueAddedAmount).toLocaleString()} 未達「{matchedBonus.tierName}」單筆升等門檻（需 NT$ {topupRule.toLocaleString()}），
                                <strong>本次儲值不會升等</strong>。
                              </div>
                            )}
                          </>
                        )
                      }
                      return (
                        <div style={{ color: '#666' }}>
                          自定義儲值金額：NT${' '}
                          {valueAddedAmount.toLocaleString()}
                          <br />
                          <span style={{ fontSize: 11 }}>
                            (未匹配到特定等級儲值回饋)
                          </span>
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>
            </Form.Item>

            <Form.Item name='discount' label='折扣%' initialValue={100}>
              <Input
                type='number'
                placeholder='折扣百分比'
                suffix='%'
                min={0}
                max={100}
                onChange={(e) => {
                  const discountValue = parseFloat(e.target.value) || 100
                  setDiscount(discountValue)
                }}
              />
            </Form.Item>

            <Form.Item label='實際收費金額'>
              <Input
                disabled
                value={
                  valueAddedAmount && discount
                    ? Math.round(
                        valueAddedAmount * (discount / 100)
                      ).toLocaleString()
                    : '0'
                }
                suffix='元'
              />
            </Form.Item>

            <Form.Item label='儲值後餘額'>
              <Input
                disabled
                value={(() => {
                  const bonusAmount = selectedTopupBonus
                    ? Number(selectedTopupBonus.bonus) || 0
                    : 0
                  // 儲值後餘額 = 當前餘額 + 實際儲值金額 + 贈送金額
                  return (
                    currentBalance +
                    valueAddedAmount +
                    bonusAmount
                  ).toLocaleString()
                })()}
                suffix='元'
              />
            </Form.Item>

            <Divider orientation='center'>付款方式</Divider>

            <Form.Item name='paymentMethods' label='付款方式'>
              <Select
                placeholder='請選擇付款方式'
                value={paymentMethod}
                onChange={(value) => {
                  setPaymentMethod(value)
                  form.setFieldsValue({ paymentMethods: value })
                  // 清除之前的輸入值
                  if (value !== '店內行動支付') {
                    setMobileCardSwipe(0)
                    form.setFieldsValue({
                      mobileCardSwipe: 0
                    })
                  }
                }}
              >
                <Option value='店內行動支付'>店內行動支付</Option>
                <Option value='由消費者APP付費'>由消費者APP付費</Option>
                <Option value='由店務系統付費'>由店務系統付費</Option>
              </Select>
            </Form.Item>

            {/*
              舊版「店內行動支付」獨立區塊已移除：
              行動支付/刷卡欄位現在永遠在下方付款明細區顯示（與 CheckoutPage 儲值 modal 一致）。
            */}

            <Form.Item label='總付款金額'>
              <Input
                disabled
                value={
                  valueAddedAmount && discount
                    ? Math.round(
                        valueAddedAmount * (discount / 100)
                      ).toLocaleString()
                    : '0'
                }
                suffix='元'
              />
            </Form.Item>

            {/* 付款明細：每行兩個欄位（現金/匯款 一組、應收款/行動支付刷卡 一組） */}
            <PaymentFieldRow>
              <Form.Item name='cash' label='現金'>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder='金額'
                  suffix='元'
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    const cashValue = value || 0
                    setCash(cashValue)
                    form.setFieldsValue({ cash: cashValue })
                    form.validateFields([
                      'cash',
                      'bankTransfer',
                      'accountsReceivable',
                      'mobileCardSwipe'
                    ])
                  }}
                />
              </Form.Item>

              <Form.Item name='bankTransfer' label='匯款'>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder='金額'
                  suffix='元'
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    const bankValue = value || 0
                    setBankTransfer(bankValue)
                    form.setFieldsValue({ bankTransfer: bankValue })
                    form.validateFields([
                      'cash',
                      'bankTransfer',
                      'accountsReceivable',
                      'mobileCardSwipe'
                    ])
                  }}
                />
              </Form.Item>

              <Form.Item name='accountsReceivable' label='應收款'>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder='金額'
                  suffix='元'
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    const arValue = value || 0
                    setAccountsReceivable(arValue)
                    form.setFieldsValue({ accountsReceivable: arValue })
                    form.validateFields([
                      'cash',
                      'bankTransfer',
                      'accountsReceivable',
                      'mobileCardSwipe'
                    ])
                  }}
                />
              </Form.Item>

              <Form.Item
                name='cardPayment'
                label={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      flexWrap: 'wrap'
                    }}
                  >
                    <span>
                      刷卡（信用卡／轉帳卡）
                      {paymentFeeSettings.cardFeeRate > 0
                        ? `（手續費 ${paymentFeeSettings.cardFeeRate}%）`
                        : ''}
                    </span>
                    <a
                      href='/settings/paymentFee'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        fontSize: 12,
                        color: '#d5728a',
                        textDecoration: 'none'
                      }}
                      title='前往「設定 → 金流手續費」修改預設費率'
                    >
                      修改
                    </a>
                  </span>
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder='金額'
                  suffix='元'
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    const cardVal = value || 0
                    setCardPayment(cardVal)
                    const mobileVal =
                      Number(form.getFieldValue('mobilePayment')) || 0
                    // 同步更新 mobileCardSwipe（向後相容）
                    const combined = cardVal + mobileVal
                    setMobileCardSwipe(combined)
                    form.setFieldsValue({
                      cardPayment: cardVal,
                      mobileCardSwipe: combined
                    })
                    // 自動算手續費
                    if (paymentFeeSettings.autoCalculate) {
                      const cardFee =
                        (cardVal * paymentFeeSettings.cardFeeRate) / 100
                      const mobileFee =
                        (mobileVal *
                          paymentFeeSettings.mobilePaymentFeeRate) /
                        100
                      const fee = Math.round((cardFee + mobileFee) * 100) / 100
                      setPaymentFee(fee)
                      form.setFieldsValue({ paymentFee: fee })
                    }
                    form.validateFields([
                      'cash',
                      'bankTransfer',
                      'accountsReceivable',
                      'cardPayment',
                      'mobilePayment'
                    ])
                  }}
                />
              </Form.Item>

              <Form.Item
                name='mobilePayment'
                label={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      flexWrap: 'wrap'
                    }}
                  >
                    <span>
                      行動支付（LINE Pay、街口等 APP）
                      {paymentFeeSettings.mobilePaymentFeeRate > 0
                        ? `（手續費 ${paymentFeeSettings.mobilePaymentFeeRate}%）`
                        : ''}
                    </span>
                    <a
                      href='/settings/paymentFee'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        fontSize: 12,
                        color: '#d5728a',
                        textDecoration: 'none'
                      }}
                      title='前往「設定 → 金流手續費」修改預設費率'
                    >
                      修改
                    </a>
                  </span>
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder='金額'
                  suffix='元'
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    const mobileVal = value || 0
                    setMobilePayment(mobileVal)
                    const cardVal =
                      Number(form.getFieldValue('cardPayment')) || 0
                    const combined = cardVal + mobileVal
                    setMobileCardSwipe(combined)
                    form.setFieldsValue({
                      mobilePayment: mobileVal,
                      mobileCardSwipe: combined
                    })
                    if (paymentFeeSettings.autoCalculate) {
                      const cardFee =
                        (cardVal * paymentFeeSettings.cardFeeRate) / 100
                      const mobileFee =
                        (mobileVal *
                          paymentFeeSettings.mobilePaymentFeeRate) /
                        100
                      const fee = Math.round((cardFee + mobileFee) * 100) / 100
                      setPaymentFee(fee)
                      form.setFieldsValue({ paymentFee: fee })
                    }
                    form.validateFields([
                      'cash',
                      'bankTransfer',
                      'accountsReceivable',
                      'cardPayment',
                      'mobilePayment'
                    ])
                  }}
                />
              </Form.Item>
            </PaymentFieldRow>

            {/* 金流手續費（自動帶入，可手動修改） */}
            <Form.Item
              name='paymentFee'
              label={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  金流手續費（將自動帶入，可自行修改）
                  <Tooltip title='依「設定 → 金流手續費」費率自動計算；如有特殊情況可手動覆寫'>
                    <QuestionCircleOutlined style={{ color: '#bdbdbd' }} />
                  </Tooltip>
                </span>
              }
              style={{ marginTop: 12 }}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder='依費率自動計算'
                prefix='NT$'
                min={0}
                precision={2}
                onChange={(value) => {
                  setPaymentFee(Number(value) || 0)
                }}
              />
            </Form.Item>
          </FormCard>
        )
      case 2:
        // 生成年份選項（當前年份前後10年）
        const currentYear = tzNow().year()
        const years = []
        for (let i = currentYear - 10; i <= currentYear + 10; i++) {
          years.push(i)
        }

        // 生成月份選項（1-12月）
        const months = []
        for (let i = 1; i <= 12; i++) {
          months.push(i)
        }

        // 根據選擇的年月計算該月的天數
        const getDaysInMonth = (year, month) => {
          if (!year || !month) return []
          const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth()
          const days = []
          for (let i = 1; i <= daysInMonth; i++) {
            days.push(i)
          }
          return days
        }

        const days = getDaysInMonth(selectedYear, selectedMonth)

        // 當日期改變時更新表單值
        const updateDateValue = (year, month, day) => {
          if (year && month && day) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(
              day
            ).padStart(2, '0')}`
            form.setFieldsValue({ date: dateStr })
          }
        }

        return (
          <FormCard title='選擇儲值日期'>
            <Form.Item name='date' label='選擇日期'>
              <FormRow>
                <Form.Item label='年'>
                  <Select
                    placeholder='選擇年份'
                    size='large'
                    value={selectedYear}
                    onChange={(year) => {
                      setSelectedYear(year)
                      // 如果選擇了年份，檢查當前選擇的日期是否在新的年月下有效
                      if (selectedMonth && selectedDay) {
                        const maxDay = dayjs(
                          `${year}-${selectedMonth}-01`
                        ).daysInMonth()
                        if (selectedDay > maxDay) {
                          setSelectedDay(maxDay)
                          updateDateValue(year, selectedMonth, maxDay)
                        } else {
                          updateDateValue(year, selectedMonth, selectedDay)
                        }
                      }
                    }}
                  >
                    {years.map((year) => (
                      <Option key={year} value={year}>
                        {year}年
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label='月'>
                  <Select
                    placeholder='選擇月份'
                    size='large'
                    value={selectedMonth}
                    onChange={(month) => {
                      setSelectedMonth(month)
                      // 如果選擇了月份，檢查當前選擇的日期是否在新的年月下有效
                      if (selectedYear && selectedDay) {
                        const maxDay = dayjs(
                          `${selectedYear}-${month}-01`
                        ).daysInMonth()
                        if (selectedDay > maxDay) {
                          setSelectedDay(maxDay)
                          updateDateValue(selectedYear, month, maxDay)
                        } else {
                          updateDateValue(selectedYear, month, selectedDay)
                        }
                      }
                    }}
                    disabled={!selectedYear}
                  >
                    {months.map((month) => (
                      <Option key={month} value={month}>
                        {month}月
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label='日'>
                  <Select
                    placeholder='選擇日期'
                    size='large'
                    value={selectedDay}
                    onChange={(day) => {
                      setSelectedDay(day)
                      if (selectedYear && selectedMonth) {
                        updateDateValue(selectedYear, selectedMonth, day)
                      }
                    }}
                    disabled={!selectedYear || !selectedMonth}
                  >
                    {days.map((day) => (
                      <Option key={day} value={day}>
                        {day}日
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </FormRow>
            </Form.Item>

            <Form.Item name='note' label='備註'>
              <Input.TextArea
                rows={6}
                placeholder='請輸入備註'
                maxLength={500}
                showCount
              />
            </Form.Item>
          </FormCard>
        )
      default:
        return null
    }
  }

  const handleNext = () => {
    // 根據當前步驟驗證對應的欄位
    const fieldsToValidate = []
    if (currentStep === 0) {
      // 步驟一：驗證客戶資訊
      fieldsToValidate.push('customerName', 'phone')
    } else if (currentStep === 1) {
      // 步驟二：驗證儲值金額和付款方式總和
      fieldsToValidate.push('valueAddedAmount')

      // 檢查是否有匹配的等級儲值回饋
      let bonusAmount = 0
      if (selectedTopupBonus) {
        bonusAmount = Number(selectedTopupBonus.bonus) || 0
      } else {
        // 如果沒有手動選擇等級儲值回饋，嘗試自動匹配
        membershipTiers.forEach((tier) => {
          if (tier.isEnabled && tier.topupBonuses) {
            const matchedBonus = tier.topupBonuses.find(
              (bonus) => Number(bonus.amount) === Number(valueAddedAmount)
            )
            if (matchedBonus) {
              if (matchedBonus.bonus >= bonusAmount) {
                bonusAmount = Number(matchedBonus.bonus) || 0
              }
            }
          }
        })
      }

      // 驗證付款方式總和
      // 總付款金額 = 客戶實際需要支付的金額（不包含贈送金額）
      const totalPayment =
        valueAddedAmount && discount
          ? Math.round(valueAddedAmount * (discount / 100))
          : 0

      if (totalPayment > 0) {
        const allValues = form.getFieldsValue(true)
        const cashAmount = allValues.cash || 0
        const bankTransferAmount = allValues.bankTransfer || 0
        const accountsReceivableAmount = allValues.accountsReceivable || 0
        // 行動支付/刷卡：永遠列入計算（不論 paymentMethod 為何）
        const mobilePaymentAmount = allValues.mobileCardSwipe || 0

        const sum =
          cashAmount +
          bankTransferAmount +
          accountsReceivableAmount +
          mobilePaymentAmount

        // 檢查是否有輸入任何付款方式
        const hasAnyPayment =
          cashAmount > 0 ||
          bankTransferAmount > 0 ||
          accountsReceivableAmount > 0 ||
          mobilePaymentAmount > 0

        if (hasAnyPayment && sum !== totalPayment) {
          Modal.error({
            title: '付款方式總和與總付款金額不符',
            content: '請確認加總金額是否正確',
            onOk: () => {
              // 停留在當前步驟，不執行任何操作
            }
          })
          return // 阻止進入下一步
        }
      }
    }
    // 步驟三：不需要驗證欄位（日期和備註都是可選的）

    if (fieldsToValidate.length > 0) {
      form
        .validateFields(fieldsToValidate)
        .then(() => {
          setCurrentStep(currentStep + 1)
        })
        .catch(() => {
          message.error('請完成必填欄位')
        })
    } else {
      // 沒有需要驗證的欄位，直接進入下一步
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrev = () => {
    setCurrentStep(currentStep - 1)
  }

  // 如果購買完成，顯示完成提示
  if (purchaseCompleted) {
    return (
      <PageContainer>
        <Result
          status='success'
          title='儲值購買完成！'
          subTitle='正在跳轉到儲值記錄頁面...'
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <StepsContainer>
        <Steps
          current={currentStep}
          items={steps}
          direction='horizontal'
          responsive={false}
        />
      </StepsContainer>

      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        preserve
        initialValues={{
          discount: 100,
          salesPersonnel: user.name,
          paymentMethods: '由店務系統付費'
        }}
      >
        {renderStepContent()}

        <ButtonGroup>
          {currentStep > 0 && (
            <Button style={{ width: '100%' }} onClick={handlePrev} size='large'>
              上一步
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button
              style={{ width: '100%' }}
              type='primary'
              onClick={handleNext}
              size='large'
            >
              下一步
            </Button>
          ) : (
            <Button
              style={{ width: '100%' }}
              type='primary'
              size='large'
              onClick={handleSubmit}
              loading={loading}
            >
              確認購買
            </Button>
          )}
        </ButtonGroup>
      </Form>
    </PageContainer>
  )
}

export default ValueAddedPurchasePage
